/**
 * `peterhof_agent`'s plan in the chat, as the Figma draws it (07.10.2026):
 * «Components / Widget / Trip» under the answer — the intro art, «Моя поездка»,
 * the day and the hours, a chevron — and the whole plan in a sheet rising from
 * the bottom («My Plan»), opened by a tap on the card.
 *
 * The sheet holds the catalogue's own `PlanTimeline`, uncut: the plan is still
 * read and edited entirely in the chat (the 18.09 decision behind `hasNoBoard`),
 * a stop's ✕ and «Удалить план» only on the newest snapshot (`useCardLive`,
 * read through the `LatestSnapshotContext` this card is rendered inside — the
 * portal keeps React context). `change_note` stays a line above the card.
 * The newest snapshot's sheet also carries «В следующий раз» and «Скачать PDF» /
 * «В календарь» (#39, #50, `export/PlanSaveBar.tsx`); an older one does not — a
 * file saved from it would be a plan the visitor has since changed.
 *
 * A modal in earnest, like the route's full-screen map: the app behind it is
 * `inert`, focus starts on ✕, Escape closes, focus returns to the card.
 */

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PlanTimeline from '../catalog/components/PlanTimeline';
import type { TripPlanProps } from '../catalog/generated/schemas';
import { plural } from './inlineTurnCards';
import { useCardLive } from '../catalog/inlineCard';
import PlanSaveBar, { NextVisit, usePdfPreload } from '../export/PlanSaveBar';
import { visitHours } from '../export/planExport';
import { usePlanRemoval } from '../catalog/planRemoval';
import { stripEmoji as plain } from '../lib/emoji';

/** «12 июня • ≈ 2,5 ч» — the day the graph named and the WHOLE visit (at the points and between them,
 *  INV-PH-113); the split is under the stops in the sheet (`PlanTimeline`'s totals line). */
export function tripLine(props: TripPlanProps): string {
  const parts: string[] = [];
  if (props.date) parts.push(props.date);
  const hours = props.totals?.hours;
  if (typeof hours === 'number' && hours > 0) parts.push(visitHours(hours));
  if (lineCountsStops(props)) {
    const stops = props.totals!.stops;
    parts.push(`${stops} ${plural(stops, 'точка', 'точки', 'точек')}`);
  }
  return parts.join(' • ');
}

/** Does `tripLine` name the number of stops (no date or no hours beside it)? */
export function lineCountsStops(props: TripPlanProps): boolean {
  const hours = props.totals?.hours;
  const said = (props.date ? 1 : 0) + (typeof hours === 'number' && hours > 0 ? 1 : 0);
  const stops = props.totals?.stops;
  return said < 2 && typeof stops === 'number' && stops > 0;
}

/** The sheet's plan: `PlanTimeline`, and on the newest snapshot (*live*) «В
 *  следующий раз» and the saves under its stops (#39, #50). */
export function SheetPlan({ props, live }: { props: TripPlanProps; live: boolean }) {
  const extra = live ? (
    <>
      <NextVisit items={props.next_visit} />
      <PlanSaveBar props={props} />
    </>
  ) : null;
  // The sheet's title already counts the stops when it has no hours: «6 точек» under «…• 6 точек» (first-user QA,
  // dev, 21.10.2026). Only the drawn copy loses it — the saves read `props` as they came.
  const drawn = lineCountsStops(props) && props.totals ? { ...props, totals: { ...props.totals, stops: 0 } } : props;
  return <PlanTimeline props={drawn} extra={extra} />;
}

export default function TripCard({ props, changeNote }: { props: TripPlanProps; changeNote?: string }) {
  const [open, setOpen] = useState(false);
  const opener = useRef<HTMLButtonElement>(null);
  const closer = useRef<HTMLButtonElement>(null);
  // Whether this is the plan's NEWEST snapshot — the one whose sheet offers ✕
  // and «Удалить план» (`PlanTimeline` asks the same question inside the sheet).
  const live = useCardLive(props.id);
  // The PDF engine is fetched while the visitor still has signal, sheet open or not (#39).
  usePdfPreload(live);
  const label = plain(props.title ?? 'Моя поездка');
  const line = plain(tripLine(props));
  // A ✕ pressed in the sheet and not answered yet: the sheet closes on the press, so the widget says it
  // (INV-PH-114) — and only on the newest snapshot, the one the ✕ was on.
  const removing = usePlanRemoval((state) => state.pending?.title);
  const widgetLine = live && removing ? `Убираю «${plain(removing)}»…` : line;

  useEffect(() => {
    if (!open) return;
    const root = document.getElementById('root');
    if (root) root.inert = true;
    closer.current?.focus();
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('keydown', escape);
      if (root) root.inert = false;
      opener.current?.focus();
    };
  }, [open]);

  return (
    <div className="turn-card turn-card--plan-full">
      {changeNote ? <p className="turn-card__change-note">Изменено: {plain(changeNote)}</p> : null}
      <button
        type="button"
        ref={opener}
        className="trip-widget"
        data-live={live}
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
      >
        <span className="trip-widget__art" aria-hidden="true">
          <img src="/figma/splash.webp" alt="" width="1200" height="741" />
        </span>
        <span className="trip-widget__text">
          <span className="trip-widget__label">{label}</span>
          {widgetLine ? <span className="trip-widget__line">{widgetLine}</span> : null}
        </span>
        <span className="trip-widget__chevron" aria-hidden="true">›</span>
      </button>
      {open
        ? createPortal(
            <div className="trip-sheet" role="dialog" aria-modal="true" aria-label={label}>
              <button
                type="button"
                className="trip-sheet__scrim"
                tabIndex={-1}
                aria-hidden="true"
                onClick={() => setOpen(false)}
              />
              <div className="trip-sheet__panel">
                <span className="trip-sheet__grip" aria-hidden="true" />
                <header className="trip-sheet__head">
                  <div>
                    <p className="trip-sheet__label">{label}</p>
                    {line ? <h2 className="trip-sheet__title">{line}</h2> : null}
                  </div>
                  <button
                    type="button"
                    ref={closer}
                    className="glass-btn glass-btn--small"
                    onClick={() => setOpen(false)}
                    title="Закрыть план"
                  >
                    <span className="mask-icon mask-icon--close" aria-hidden="true" />
                    <span className="visually-hidden">Закрыть план</span>
                  </button>
                </header>
                {/* Every button in the plan SENDS — a stop's ✕, «Удалить план», a
                    card action — and the guide answers in the chat behind this
                    sheet (a confirmation, the new plan). So a button press
                    closes the sheet once its own handler has run (bubbling);
                    a ticket link opens a tab and leaves the sheet as it was,
                    and so does a save (`data-sheet-stays`): it sends nothing,
                    and its «Готовлю PDF…» or failure is said in the sheet. */}
                <div
                  className="trip-sheet__body"
                  onClick={(event) => {
                    const button = (event.target as HTMLElement).closest('button');
                    if (button && !button.closest('[data-sheet-stays]')) setOpen(false);
                  }}
                >
                  <SheetPlan props={props} live={live} />
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
